import React, { useEffect, useMemo, useState } from "react";
import { nexusEventLabel, sortNexusEvents } from "./nexusEventLabels";
import styled from "styled-components";
import { Col, Row, Spin, Tabs, Tooltip, Upload } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment";
import dayjs from "dayjs";
import {
  ButtonFilled,
  ButtonOutlined,
  Buttontransparent,
} from "../../Reusablecomponents/UIButtons";
import { StyledTable } from "../../Reusablecomponents/UITable/UITable";
import UIModal from "../../Reusablecomponents/UIModal";
import MapComponent from "../../Reusablecomponents/MapComponent";
import FilesTable from "../../Reusablecomponents/FilesTable";
import ChatComponent from "../../Reusablecomponents/Comment/ChatComponent";
import { CALL_API } from "../../Common/api";
import {
  chargetype,
  DispatchType,
  FinishType,
  shipmentType,
  TripTypeEnum,
  PanTripTypeEnum,
} from "../../Common/constant";
import { ReactComponent as EditIcon } from "../../assets/images/Editicon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-icon.svg";
import { ReactComponent as MapIcon } from "../../assets/images/Group 1000004561.svg";

export const NexusDetailsWrapper = styled.div`
  label {
    color: rgb(152, 161, 181);
    font-size: 14px;
    display: block;
    margin-bottom: 4px;
  }
  p {
    margin-bottom: 0px;
    line-height: normal;
    font-size: 16px;
    font-weight: 400;
  }
  .details_header {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
  .details_header_left,
  .details_header_right {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .back_btn {
    border: none;
    outline: none;
    background-color: rgb(235, 247, 237);
    border-radius: 50%;
    padding: 17px 13px;
  }
  .back_btn .anticon {
    color: rgb(44, 150, 61);
    transform: scale(0.8);
  }
  .icon_btn {
    border-radius: 50%;
    padding: 9px;
    border: 1px solid rgb(231, 231, 237);
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgb(235, 247, 237);
  }
  .bulk_btn {
    height: 38px;
    gap: 8px;
    border: none;
    outline: none;
    border-radius: 26px;
    padding: 9px 16px;
    font-weight: 500;
    font-size: 16px;
    display: inline-flex;
    align-items: center;
    background-color: #e8ffec !important;
    color: #2c963d !important;
  }
  .update_complete_btn {
    height: 38px;
    gap: 8px;
    border: none;
    outline: none;
    border-radius: 26px;
    padding: 9px 16px;
    font-weight: 500;
    font-size: 16px;
    display: inline-flex;
    align-items: center;
    background-color: rgba(255, 230, 167, 0.4) !important;
    color: rgba(194, 142, 8, 1) !important;
  }
  .status_pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
  }
  .status_dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }
  .details_grid {
    background-color: rgb(248, 248, 248);
    border-radius: 12px;
    margin: 16px 0 20px;
    padding: 10px;
    display: flex;
    flex-wrap: wrap;
  }
  .details_item {
    width: 25%;
    padding: 10px;
  }
  .details_value {
    font-size: 16px;
    color: #363636;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
  }
  @media (max-width: 991px) {
    .details_item {
      width: 33.33%;
    }
  }
  @media (max-width: 768px) {
    .details_item {
      width: 50%;
    }
  }
  @media (max-width: 480px) {
    .details_item {
      width: 100%;
    }
  }
  .tab_row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
  }
  .nexus_tabs.ant-tabs-top > .ant-tabs-nav::before {
    border-bottom: unset !important;
  }
  .nexus_tabs .ant-tabs-nav-list {
    display: flex;
    gap: 15px;
  }
  .nexus_tabs .ant-tabs-tab {
    font-size: 15px !important;
    color: #5f6d8c !important;
    border-radius: 27px !important;
  }
  .nexus_tabs .ant-tabs-tab-active {
    background-color: #d9d9d9 !important;
  }
  .nexus_tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #2c963d !important;
  }
  .nexus_tabs .ant-tabs-content-holder {
    display: none;
  }
`;

// ─── shared helpers (also used by nexusTripDetails) ─────────────────────────

// nexus rows come back with mixed-case keys; normalise to lower case
export const lowerKeys = (row) =>
  Object.fromEntries(
    Object.entries(row || {}).map(([k, v]) => [k.toLowerCase(), v]),
  );

export const firstValue = (...values) =>
  values.find((v) => v !== undefined && v !== null && v !== "");

// Nexus times are stored in UTC; show them in Nairobi time
export const formatNairobiTime = (value) =>
  value && moment.utc(value).isValid()
    ? moment.utc(value).utcOffset(180).format("Do MMM YYYY; hh:mm A")
    : "---";

export const enumName = (list, value) =>
  value === undefined || value === null || value === ""
    ? undefined
    : list?.find((item) => String(item.value) === String(value))?.name;

export const nexusStatus = (status) => {
  const s = Number(status);
  if (s === 4) return { label: "Completed", color: "#2C963D" };
  if (s === 3) return { label: "In-Progress", color: "#FA8C16" };
  if (s === 2) return { label: "Rejected", color: "#ff4d4f" };
  if (s === 0 || s === 1) return { label: "Assigned", color: "#E4A400" };
  return { label: "---", color: "#E4A400" };
};

// geofence_id(s) → name(s) using the Nexus fence list; unknown ids are shown as-is
export const fenceNames = (fenceList, value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const ids = Array.isArray(value)
    ? value.flat()
    : String(value)
        .split(",")
        .map((s) => s.trim());
  const names = ids
    .filter((id) => id !== "" && id != null)
    .map(
      (id) =>
        (fenceList || []).find((f) => String(f.geofence_id) === String(id))
          ?.name || id,
    );
  return names.length ? names.join(", ") : undefined;
};

// NexusDeliveryEvents rows → shape MapComponent's locationsTLAI understands.
// With FENCENAME set it shows MODON minus 3h, so pass Nairobi wall time + 3h.
export const toMapEvents = (events, shipmentType) =>
  (events || [])
    .filter((ev) => ev.lat && ev.lng)
    .map((ev) => ({
      LAT: ev.lat,
      LNG: ev.lng,
      EventName: nexusEventLabel(ev.eventname, shipmentType),
      FENCENAME: ev.fencename || "-",
      MODON: ev.eventtime
        ? moment
            .utc(ev.eventtime)
            .utcOffset(180)
            .add(3, "hours")
            .format("YYYY-MM-DDTHH:mm:ss")
        : null,
    }));

// GET nexus/nexustrackdetails → { trip, consignments, events } (keys lower-cased)
export const fetchNexusTrackDetails = async (tripId, mcId) => {
  if (tripId === undefined || tripId === null || tripId === "") {
    return { trip: {}, consignments: [], events: [] };
  }
  const res = await CALL_API(
    "GET",
    `nexus/nexustrackdetails?tripId=${tripId}&mcId=${mcId}`,
  );
  const data = res?.data || {};
  return {
    trip: data.trip ? lowerKeys(data.trip) : {},
    consignments: (Array.isArray(data.consignments) ? data.consignments : []).map(
      lowerKeys,
    ),
    events: sortNexusEvents(
      (Array.isArray(data.events) ? data.events : []).map(lowerKeys),
    ),
  };
};

// Files tab upload (same flow as the AI details pages, with the Nexus refTab)
export const FileUploadButton = ({ refTab, refId, onUploaded, disabled }) => {
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);

  const upload = (file, onSuccess, lat = "", lng = "") => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      onSuccess();
      await CALL_API("POST", "common/file/addFile", {
        title: file.name,
        fileSize: file.size,
        mimeType: 1,
        crBy: userInfo?.UID,
        attKey: "testKey",
        refTab,
        refId,
        lat,
        lng,
        crOn: new Date(),
        fileName: file.name,
        fileType: file.name.split(".").pop(),
        file: reader.result,
      });
      onUploaded?.();
    };
    reader.onerror = () => console.error("File reading failed:", reader.error);
  };

  return (
    <Upload
      disabled={disabled}
      showUploadList={false}
      customRequest={({ file, onSuccess }) => {
        navigator.geolocation.getCurrentPosition(
          ({ coords }) =>
            upload(file, onSuccess, String(coords.latitude), String(coords.longitude)),
          () => upload(file, onSuccess),
        );
      }}
    >
      <Tooltip title={disabled ? "No permission" : ""}>
        <ButtonFilled
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          className="upload-style1"
        >
          + Add
        </ButtonFilled>
      </Tooltip>
    </Upload>
  );
};

export const DetailsGrid = ({ items }) => (
  <div className="details_grid">
    {items.map(({ label, value }) => (
      <div className="details_item" key={label}>
        <label>{label}</label>
        <p className="details_value">{firstValue(value) ?? "---"}</p>
      </div>
    ))}
  </div>
);

// ─── Nexus consignment details ─────────────────────────────────────────────

const NEXUS_CONSIGN_REFTAB = "NEXUSCONSIGN";

const NexusConsignDetails = ({
  consignmentDetails,
  setConsignee,
  setFormData,
  setDrawerOpen,
  setIsEdit,
  handleDelete,
}) => {
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);
  const nexusFenceList = useSelector(
    (state) => state?.authTracking?.nexusFenceList,
  );
  const transporterList = useSelector(
    (state) => state?.authTracking?.transporterList,
  );
  const reduxPermissions = useSelector(
    (state) => state?.authTracking?.myPermissionList || {},
  );
  const hasPermission = (seccode, action) =>
    Array.isArray(reduxPermissions?.[seccode]) &&
    reduxPermissions[seccode].includes(action);
  const updateAllowed = useMemo(
    () => hasPermission("NE_CONSIGN", "UPC"),
    [reduxPermissions],
  );
  const deleteAllowed = useMemo(
    () => hasPermission("NE_CONSIGN", "DC"),
    [reduxPermissions],
  );
  const fileUpAllowed = useMemo(
    () => hasPermission("NE_CONSIGN", "UF"),
    [reduxPermissions],
  );

  const raw = lowerKeys(consignmentDetails?.rawTrip || {});
  const consignId = firstValue(consignmentDetails?.ConsignId, raw.consignid);
  const tripId = firstValue(consignmentDetails?.tripId, raw.tripid);

  const [activeKey, setActiveKey] = useState("1");
  const [loading, setLoading] = useState(false);
  const [trackData, setTrackData] = useState({
    trip: {},
    consignments: [],
    events: [],
  });
  const [filesList, setFilesList] = useState([]);
  const [commentsList, setCommentsList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const loadTrackDetails = async () => {
    setLoading(true);
    try {
      setTrackData(await fetchNexusTrackDetails(tripId, userInfo?.MCID));
    } catch (error) {
      console.error("[NexusConsignDetails] track details error:", error);
      setTrackData({ trip: {}, consignments: [], events: [] });
    } finally {
      setLoading(false);
    }
  };

  const getFileData = async () => {
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "ai/trip/getFileTab", {
        refTab: NEXUS_CONSIGN_REFTAB,
        refId: consignId,
      });
      setFilesList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[NexusConsignDetails] files error:", error);
      setFilesList([]);
    } finally {
      setLoading(false);
    }
  };

  const getCommentData = async () => {
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "ai/trip/getcommentTab", {
        refTab: NEXUS_CONSIGN_REFTAB,
        refId: consignId,
      });
      setCommentsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[NexusConsignDetails] comments error:", error);
      setCommentsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrackDetails();
  }, [tripId]);

  useEffect(() => {
    if (activeKey === "3") getFileData();
    if (activeKey === "4") getCommentData();
  }, [activeKey]);

  // Latest DB row for this consignment (falls back to the list row)
  const consign = useMemo(() => {
    const row = trackData.consignments.find(
      (c) => String(c.consignid) === String(consignId),
    );
    return { ...raw, ...(row || {}) };
  }, [trackData.consignments, consignmentDetails]);

  // Events for this consignment only (older events may have no consignId)
  const consignEvents = useMemo(() => {
    const own = trackData.events.filter(
      (ev) => String(ev.consignid) === String(consignId),
    );
    return own.length ? own : trackData.events;
  }, [trackData.events, consignId]);

  // 1 = Delivery, 2 = Collection: the events are named differently
  const shipType = firstValue(consign.shipmenttype, trackData.trip.shipmenttype);
  const mapEvents = useMemo(
    () => toMapEvents(consignEvents, shipType),
    [consignEvents, shipType],
  );

  const status = nexusStatus(firstValue(consign.status, trackData.trip.status));
  const tripTypeList = userInfo?.MCID === 72 ? PanTripTypeEnum : TripTypeEnum;

  const detailItems = [
    { label: "Trip Code", value: firstValue(trackData.trip.tripcode, consign.code) },
    {
      label: "Consignor",
      value: firstValue(
        consign.consignorname,
        fenceNames(nexusFenceList, consign.consigner),
      ),
    },
    {
      label: "Consignee",
      value: firstValue(
        consign.consigneename,
        fenceNames(nexusFenceList, consign.consignee),
      ),
    },
    {
      label: "Transporter",
      value: firstValue(
        transporterList?.find(
          (t) => String(t.ID) === String(consign.transporter),
        )?.Name,
        consign.transporter,
      ),
    },
    { label: "Vehicle", value: firstValue(consign.vehiclename, trackData.trip.vehiclename) },
    { label: "Driver Name", value: firstValue(consign.drivername, trackData.trip.drivername) },
    { label: "Device", value: firstValue(consign.deviceid, trackData.trip.device) },
    { label: "Charge Type", value: enumName(chargetype, consign.chargetype) },
    { label: "Trip Type", value: enumName(tripTypeList, consign.triptype) },
    { label: "Shipment Type", value: enumName(shipmentType, consign.shipmenttype) },
    { label: "Dispatch Type", value: enumName(DispatchType, consign.dispatchtype) },
    { label: "Completion Rule", value: enumName(FinishType, consign.completiontype) },
    { label: "Pickup Date & Time", value: formatNairobiTime(consign.pickuptime) },
    { label: "Dropoff Date & Time", value: formatNairobiTime(consign.droptime) },
    { label: "Delivery Note Number", value: consign.deliveryno },
    { label: "Container No", value: consign.containerno },
    // { label: "Location", value: consign.location },
    { label: "Waybill", value: consign.fwaybill },
    { label: "Created On", value: formatNairobiTime(consign.cron) },
  ];

  const eventColumns = [
    {
      title: "Event",
      dataIndex: "eventname",
      key: "eventname",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {record.lat && record.lng && (
            <MapIcon
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
              onClick={() => setSelectedEvent(record)}
            />
          )}
          <span>{nexusEventLabel(text, shipType) || "---"}</span>
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "fencename",
      key: "fencename",
      render: (text, record) => (
        // inline spans, not stacked <p> tags: the stacked version rendered the
        // fence name and "Drop N" on top of each other in this table
        <span>
          {text || "---"}
          {record.dropseq != null && (
            <span style={{ color: "#979797", fontSize: 14, marginLeft: 8 }}>
              Drop {record.dropseq}
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Event Time",
      dataIndex: "eventtime",
      key: "eventtime",
      render: (value) => formatNairobiTime(value),
    },
    {
      title: "Updated by",
      key: "updatedby",
      render: () => "TrackRight AI",
    },
  ];

  const handleEdit = () => {
    setFormData({
      ConsignId: consignId,
      TripId: tripId,
      // consignorFenceList/consigneeFenceList only load once formData.company is
      // set (see the fence-fetch effect in nexus-AI.js), so without this the
      // Consignor/Consignee dropdowns have no options to match against.
      company: firstValue(consign.companyid, trackData.trip.companyid, userInfo?.MCID),
      consignor: String(consign.consigner ?? ""),
      fence: String(consign.consignee ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((id, idx) => ({ [idx]: id })),
      transporter: consign.transporter,
      // chargetype dropdown options are numeric; the saved value can come back
      // as a string, which fails strict Select value matching.
      chargetype:
        consign.chargetype === undefined || consign.chargetype === null || consign.chargetype === ""
          ? undefined
          : Number(consign.chargetype),
      pdate: consign.pickuptime ? dayjs(consign.pickuptime) : "",
      ddate: consign.droptime ? dayjs(consign.droptime) : "",
      ReferenceNo: consign.deliveryno,
      ContainerNo: consign.containerno,
      // saved on the trip, so only consignments with a vehicle have one
      tripallowance:
        trackData.trip?.tripallowance != null && trackData.trip?.tripallowance !== ""
          ? String(trackData.trip.tripallowance)
          : "",
    });
    setIsEdit(true);
    setDrawerOpen(true);
  };

  return (
    <NexusDetailsWrapper>
      <Row gutter={16}>
        <Col xs={24}>
          <div className="details_header">
            <div className="details_header_left">
              <ButtonOutlined
                onClick={() => setConsignee(false)}
                className="back_btn"
              >
                <LeftOutlined />
              </ButtonOutlined>
              <p>Consign Code - {firstValue(consign.code, consignmentDetails?.Code) || "---"}</p>
              <span className="status_pill" style={{ color: status.color }}>
                <span
                  className="status_dot"
                  style={{ backgroundColor: status.color }}
                />
                {status.label}
              </span>
            </div>
            <div className="details_header_right">
              <Tooltip title={!updateAllowed ? "No permission" : ""}>
                <ButtonOutlined className="icon_btn">
                  <EditIcon
                    style={{ cursor: updateAllowed ? "pointer" : "not-allowed" }}
                    onClick={() => updateAllowed && handleEdit()}
                  />
                </ButtonOutlined>
              </Tooltip>
              <Tooltip title={!deleteAllowed ? "No permission" : ""}>
                <ButtonOutlined className="icon_btn">
                  <DeleteIcon
                    style={{ cursor: deleteAllowed ? "pointer" : "not-allowed" }}
                    onClick={() => deleteAllowed && setIsDeleteModalVisible(true)}
                  />
                </ButtonOutlined>
              </Tooltip>
            </div>
          </div>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <DetailsGrid items={detailItems} />

        <div className="tab_row">
          <Tabs
            className="nexus_tabs"
            type="card"
            activeKey={activeKey}
            onChange={setActiveKey}
            items={[
              { label: "Event", key: "1" },
              { label: "Location", key: "2" },
              { label: "Files", key: "3" },
              { label: "Comment", key: "4" },
            ]}
          />
          {activeKey === "3" && (
            <FileUploadButton
              refTab={NEXUS_CONSIGN_REFTAB}
              refId={consignId}
              onUploaded={getFileData}
              disabled={!fileUpAllowed}
            />
          )}
        </div>

        {activeKey === "1" && (
          <StyledTable
            dataSource={consignEvents.map((ev, i) => ({ ...ev, key: ev.id ?? i }))}
            columns={eventColumns}
            pagination={false}
          />
        )}
        {activeKey === "2" && (
          <MapComponent locationsTLAI={mapEvents} LocationName={mapEvents} />
        )}
        {activeKey === "3" && (
          <FilesTable filesList={filesList} getFileData={getFileData} />
        )}
        {activeKey === "4" && (
          <ChatComponent
            data={commentsList}
            user={{ UID: userInfo?.UID }}
            getData={getCommentData}
            id={consignId}
            RefTab={NEXUS_CONSIGN_REFTAB}
            setLoading={setLoading}
          />
        )}
      </Spin>

      <UIModal
        customHeight={500}
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        closeButton={false}
      >
        {selectedEvent && (
          <MapComponent
            locationsTL={[
              {
                EventName: nexusEventLabel(selectedEvent.eventname, shipType),
                Lat: Number(selectedEvent.lat),
                Lng: Number(selectedEvent.lng),
                MODON: selectedEvent.eventtime,
              },
            ]}
            pinlocation={[
              { lat: Number(selectedEvent.lat), lng: Number(selectedEvent.lng) },
            ]}
            isminimap={true}
          />
        )}
      </UIModal>

      <UIModal
        width={500}
        open={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        closeButton={false}
        size="md"
        header={false}
        footer={false}
      >
        <div className="text-center">
          <h1>Are you sure,</h1>
          <h6>You want to delete this consignment?</h6>
          <div className="mt-2 w-100 d-flex justify-content-center">
            <ButtonFilled
              onClick={async () => {
                setIsDeleteModalVisible(false);
                await handleDelete(consignId);
                setConsignee(false);
              }}
            >
              Yes
            </ButtonFilled>
            <Buttontransparent onClick={() => setIsDeleteModalVisible(false)}>
              No
            </Buttontransparent>
          </div>
        </div>
      </UIModal>
    </NexusDetailsWrapper>
  );
};

export default NexusConsignDetails;
